/* -----------------Globals--------------- */
import { formatDate, getCalendarDaysUntil } from "@memorang/helpers/date";
import React, {
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react";

/* -----------------Child components--------------- */
import {
	setBaseEventProperties,
	trackCustomEvent,
	updateUserProperties,
} from "analytics";

/* -----------------Constants--------------- */
import { events } from "@constants/tracking";

import { cacheKeys } from "@constants/cache-keys";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { getAuthToken, getLogicalUserId } from "@helpers/auth-provider";
import { getBaseCacheKey } from "@helpers/common";
import { isAxiosError } from "@helpers/lib/axios";
import { useAppStore } from "@hooks/useAppStore";
import useCache from "@hooks/useCache";
import { useGlobalStore } from "@hooks/useGlobalStore";
import type { AppContextCache } from "@memorang/constants/apps";
import type { ExamDetails } from "@memorang/types";
import { useQueryClient } from "@tanstack/react-query";
import { fetchExamDetails, updateExamDetails } from "api/commerce/exam-service";
import { examsListQueryOptions, setCachedExamDate } from "api/exams-query";
import {
	constructClientExamContext,
	retrieveExamContextFromKV,
	storeExamContextInKV,
} from "../helpers";
/* -----------------Local modules--------------- */
import { defaultExamContext } from "./constants";
import type { CurrentExamContext } from "./types";

type ContextValue = {
	currentExamContext: CurrentExamContext;
	isExamContextLoading: boolean;
	updateCurrentExamContext: (examContext: CurrentExamContext) => void;
	updateExamDate: (examDate: Date) => void;
	handleSwitchExamContext: (examDetails: ExamDetails) => Promise<void>;
};

const initExamContext = {
	studyPackProductId: "",
	examName: "",
	examId: "",
	studyPackProductNumericId: 0,
	projectId: 0,
	organizationId: 0,
	studyPackCollectionId: 0,
	practiceTestProductId: "",
};

const Context = createContext<ContextValue>({
	currentExamContext: initExamContext,
	isExamContextLoading: true,
	updateCurrentExamContext: () => {
		//
	},
	updateExamDate: () => {
		//
	},
	handleSwitchExamContext: async () => {
		//
	},
});

const ExamContextProvider = ({ children }: { children: ReactNode }) => {
	const [currentExamContext, setCurrentExamContext] =
		useState<CurrentExamContext>(initExamContext);
	const [isExamContextLoading, setIsExamContextLoading] = useState(true);
	const contextVersionRef = useRef(0);
	const externalFlowStartVersionRef = useRef<number | null>(null);
	const syncRunIdRef = useRef(0);

	const { userLogicalId, isLoading, isSignedIn } = useAuthContext();
	const isAuthFlowExamContextSwitching = useGlobalStore(
		(state) => state.isAuthFlowExamContextSwitching,
	);
	const appId = useAppStore((state) => state.app?.id) || "";
	const queryClient = useQueryClient();

	useEffect(() => {
		if (!currentExamContext.examId) {
			setBaseEventProperties({});
			return;
		}
		setBaseEventProperties({
			appId,
			examId: currentExamContext.examId,
			examName: currentExamContext.examName,
			projectId: currentExamContext.projectId,
			organizationId: currentExamContext.organizationId,
		});
	}, [appId, currentExamContext]);

	const baseCacheKey = useMemo(
		() =>
			appId && userLogicalId
				? getBaseCacheKey({
						orgId: defaultExamContext.organizationId,
						projectId: defaultExamContext.projectId,
						appId,
					})
				: "",
		[appId, userLogicalId],
	);

	const appContextCacheKey = baseCacheKey
		? `${baseCacheKey}:user-${userLogicalId}:app-context`
		: "";

	const { setCache: setSelectedStateIdCache } = useCache<AppContextCache>({
		key: appContextCacheKey || "",
		defaultData: { selectedStates: [{ code: "", relatedExamsIds: [] }] },
		enabled: Boolean(appContextCacheKey),
		shouldOptimisticUpdate: true,
	});
	const appContextData = queryClient.getQueryData<AppContextCache>([
		cacheKeys.cache,
		appContextCacheKey || "",
	]);

	const selectedState = useMemo(
		() => appContextData?.selectedStates?.[0],
		[appContextData],
	);

	const relatedExamsIds = useMemo(
		() => selectedState?.relatedExamsIds ?? [],
		[selectedState],
	);

	const updateCurrentExamContext = useCallback(
		async (data: CurrentExamContext) => {
			contextVersionRef.current += 1;
			setCurrentExamContext(data);

			const daysLeft = getCalendarDaysUntil(data.examDate);

			const userId = userLogicalId ?? getLogicalUserId();
			if (userId) {
				await storeExamContextInKV(data);
			}
			trackCustomEvent({
				eventName: events.examContextSet,
				examName: data.examName,
				examId: data.examId,
			});
			updateUserProperties({
				mem_exam_name: data.examName,
				mem_exam_id: String(data.examId),
				mem_exam_date: data.examDate ?? "not-set",
				mem_exam_interval: `${daysLeft} days`,
			});
		},
		[userLogicalId],
	);

	const setDefaultExamContext = useCallback(() => {
		setCurrentExamContext(defaultExamContext);
	}, []);

	const getExamContextWithDate = useCallback(
		async (cachedContext: CurrentExamContext): Promise<CurrentExamContext> => {
			const token = await getAuthToken();
			if (!token) {
				return cachedContext;
			}

			try {
				const examDetails = await fetchExamDetails({
					examId: cachedContext.examId,
				});

				const studyPackCollectionId =
					cachedContext.studyPackCollectionId ||
					examDetails.exam.studyPackCollection[0];
				const freeStudyPackCollectionId =
					examDetails.exam.freeStudypackCollectionId ??
					cachedContext.freeStudyPackCollectionId;

				const practiceTest = examDetails.exam.bundles.find(
					(item) => item.bundle.bundleType === "PRACTICE_TEST",
				)?.bundle;
				const prepBundle = examDetails.exam.bundles.find(
					(item) => item.bundle.bundleType === "PREP",
				)?.bundle;

				const practiceTestProductNumericId =
					cachedContext.practiceTestProductNumericId || practiceTest?.numericId;
				const prepNumericId =
					cachedContext.prepNumericId || prepBundle?.numericId;
				const prepId = cachedContext.prepId || prepBundle?.id;

				return {
					...cachedContext,
					examIcon: examDetails.exam.icon,
					examDate: examDetails.metadata.examDate,
					studyPackCollectionId,
					freeStudyPackCollectionId,
					practiceTestProductNumericId,
					prepNumericId,
					prepId,
				};
			} catch (error) {
				if (isAxiosError(error) && error.response?.status === 401) {
					return cachedContext;
				}
				console.error("Failed to fetch exam details:", error);
				return cachedContext;
			}
		},
		[],
	);

	const createExamContextFromPurchased = useCallback(
		(examDetails: ExamDetails): CurrentExamContext => {
			const constructedExamContext = constructClientExamContext(examDetails);

			return {
				...constructedExamContext,
				projectId: defaultExamContext.projectId,
				organizationId: defaultExamContext.organizationId,
				legacySessionsCollectionId:
					defaultExamContext.legacySessionsCollectionId,
			};
		},
		[],
	);

	const handleCachedExamContext = useCallback(
		async (cachedExamContext: CurrentExamContext, isStale: () => boolean) => {
			setCurrentExamContext(cachedExamContext);
			setIsExamContextLoading(false);
			const updatedContext = await getExamContextWithDate(cachedExamContext);

			if (isStale()) {
				return;
			}

			const wasUpdated =
				(!cachedExamContext.studyPackCollectionId &&
					updatedContext.studyPackCollectionId) ||
				(!cachedExamContext.freeStudyPackCollectionId &&
					updatedContext.freeStudyPackCollectionId) ||
				(!cachedExamContext.practiceTestProductNumericId &&
					updatedContext.practiceTestProductNumericId) ||
				(!cachedExamContext.prepNumericId && updatedContext.prepNumericId) ||
				(!cachedExamContext.prepId && updatedContext.prepId);

			if (wasUpdated) {
				await updateCurrentExamContext(updatedContext);
			} else {
				setCurrentExamContext(updatedContext);
			}
		},
		[getExamContextWithDate, updateCurrentExamContext],
	);

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
	const syncExamContext = useCallback(async () => {
		if (isLoading) {
			return;
		}

		if (!(isSignedIn && userLogicalId)) {
			setIsExamContextLoading(false);
			return;
		}

		contextVersionRef.current += 1;
		const version = contextVersionRef.current;
		const isStale = () => contextVersionRef.current !== version;

		syncRunIdRef.current += 1;
		const syncRunId = syncRunIdRef.current;

		setIsExamContextLoading(true);

		const mergeCachedExamContext = (
			cachedExamContext: CurrentExamContext,
		): CurrentExamContext => ({
			...cachedExamContext,
			projectId: defaultExamContext.projectId,
			organizationId: defaultExamContext.organizationId,
			documentsCollectionId: defaultExamContext.documentsCollectionId,
			legacySessionsCollectionId: defaultExamContext.legacySessionsCollectionId,
		});

		try {
			const cachedExamContext = await retrieveExamContextFromKV();
			const token = await getAuthToken();

			if (isStale()) {
				return;
			}

			if (cachedExamContext?.examId) {
				const mergedCachedContext = mergeCachedExamContext(cachedExamContext);

				if (!token) {
					setCurrentExamContext(mergedCachedContext);
					return;
				}

				await handleCachedExamContext(mergedCachedContext, isStale);
				return;
			}

			if (!token) {
				return;
			}

			const latestExams = await queryClient.fetchQuery(
				examsListQueryOptions(userLogicalId),
			);

			if (isStale()) {
				return;
			}

			const purchasedExam = latestExams.find((item) =>
				item.exam.bundles.some(
					(bundle) => bundle.metadata?.subscription?.status === "ACTIVE",
				),
			);

			if (!purchasedExam) {
				setDefaultExamContext();
				return;
			}

			const newExamContext = createExamContextFromPurchased(purchasedExam);
			await storeExamContextInKV(newExamContext);

			if (isStale()) {
				return;
			}

			setCurrentExamContext(newExamContext);
		} catch (_error) {
			if (!isStale()) {
				setDefaultExamContext();
			}
		} finally {
			if (syncRunIdRef.current === syncRunId) {
				setIsExamContextLoading(false);
			}
		}
	}, [
		userLogicalId,
		isSignedIn,
		setDefaultExamContext,
		createExamContextFromPurchased,
		handleCachedExamContext,
		isLoading,
		queryClient,
	]);
	useEffect(() => {
		if (isAuthFlowExamContextSwitching) {
			externalFlowStartVersionRef.current = contextVersionRef.current;
			setIsExamContextLoading(false);
			return;
		}

		const externalFlowStartVersion = externalFlowStartVersionRef.current;
		externalFlowStartVersionRef.current = null;

		const didExternalFlowSwitchContext =
			externalFlowStartVersion !== null &&
			contextVersionRef.current > externalFlowStartVersion;

		if (didExternalFlowSwitchContext) {
			return;
		}

		syncExamContext();
	}, [syncExamContext, isAuthFlowExamContextSwitching]);

	const updateExamDate = useCallback(
		(examDate: Date) => {
			if (examDate) {
				const formattedDate = formatDate(examDate, "en-US"); // Always store date as en-US format

				if (currentExamContext.examId) {
					trackCustomEvent({
						eventName: events.examDateSet,
						examDate: formattedDate,
					});
					updateExamDetails({
						examId: currentExamContext.examId,
						metadata: {
							examDate: formattedDate,
						},
					}).then(() => {
						if (userLogicalId) {
							setCachedExamDate(
								queryClient,
								userLogicalId,
								currentExamContext.examId,
								formattedDate,
							);
						}
					});

					updateCurrentExamContext({
						...currentExamContext,
						examDate: formattedDate,
					});
				}
			}
		},
		[currentExamContext, updateCurrentExamContext, queryClient, userLogicalId],
	);

	const handleSwitchExamContext = useCallback(
		async (examDetails: ExamDetails) => {
			const isAssociatedWithTheCurrentState = relatedExamsIds.includes(
				examDetails.exam.id,
			);
			if (!isAssociatedWithTheCurrentState) {
				setSelectedStateIdCache({
					selectedStates: [
						{
							code: examDetails.exam.state?.code ?? "",
							relatedExamsIds: examDetails.exam.relatedExams ?? [],
						},
					],
				});
			}
			const constructedExamContext = constructClientExamContext(examDetails);

			await updateCurrentExamContext({
				...constructedExamContext,
				projectId: defaultExamContext.projectId,
				organizationId: defaultExamContext.organizationId,
				legacySessionsCollectionId:
					defaultExamContext.legacySessionsCollectionId,
			});
		},
		[updateCurrentExamContext, setSelectedStateIdCache, relatedExamsIds],
	);

	const value = useMemo(() => {
		return {
			currentExamContext,
			isExamContextLoading,
			updateCurrentExamContext,
			updateExamDate,
			handleSwitchExamContext,
		};
	}, [
		currentExamContext,
		isExamContextLoading,
		handleSwitchExamContext,
		updateCurrentExamContext,
		updateExamDate,
	]);

	return <Context.Provider value={value}>{children}</Context.Provider>;
};

const useExamContext = () => {
	const context = React.useContext(Context);
	if (context === undefined) {
		throw new Error("useExamContext must be used within a ExamContextProvider");
	}
	return context;
};

export { ExamContextProvider, useExamContext };
